import { DragEndEvent } from '@dnd-kit/core';
import { useEffect, useMemo, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { isAdviserArea as selectIsAdviserArea } from '../../../CommonSelectors/CommonAreaSelector';
import { MESSAGES } from '../../../CommonConstants';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { PLANS_MIN_COUNT } from '../../../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/RetirementSideBySide.meta';
import { useComparePlansDisplayOrderQuery } from '../../../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/api/useComparePlansDisplayOrderQuery';
import { useSaveComparePlansDisplayOrderMutation } from '../../../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/api/useSaveComparePlansDisplayOrderMutation';
import {
  arePlanIdOrdersEqual,
  orderPlanIdsByDisplayOrder,
  resolveOrderedPlanIds,
} from '../../../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/utils/orderPlanIdsByDisplayOrder';

interface FormValues {
  planFields: { id: string; selected: boolean }[];
}

export interface ComparePlansProps {
  clientId: string;
  data: {
    plans: { id: string; name: string }[];
    selectedPlans: string[];
  };
  onSave(selectedPlans: string[]): void;
  onClose(): void;
}

export const useComparePlans = ({
  clientId,
  data: { plans, selectedPlans },
  onSave,
  onClose,
}: ComparePlansProps) => {
  const isAdviserArea = useSelector(selectIsAdviserArea);
  const planIds = useMemo(() => plans.map(plan => plan.id), [plans]);
  const hasUserReorderedRef = useRef(false);

  const { data: displayOrder, isSuccess: isDisplayOrderSuccess } = useComparePlansDisplayOrderQuery(
    {
      clientId,
      enabled: planIds.length >= PLANS_MIN_COUNT,
    },
  );

  const orderedPlanIds = useMemo(() => orderPlanIdsByDisplayOrder(planIds, displayOrder), [
    displayOrder,
    planIds,
  ]);

  const formBag = useForm<FormValues>({
    defaultValues: {
      planFields: orderedPlanIds.map(id => ({
        id,
        selected: selectedPlans.includes(id),
      })),
    },
  });
  const { control, watch, reset, getValues, handleSubmit } = formBag;

  const { fields, move } = useFieldArray({
    control,
    name: 'planFields',
    keyName: 'key',
  });
  const watchFieldArray = watch('planFields');
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  useEffect(() => {
    if (!isDisplayOrderSuccess) {
      return;
    }

    const currentFields = getValues('planFields');
    const selectedById = new Map(currentFields.map(field => [field.id, field.selected]));
    const currentOrder = currentFields.map(field => field.id);
    const nextOrder = resolveOrderedPlanIds(
      planIds,
      displayOrder,
      currentOrder,
      hasUserReorderedRef.current,
    );

    if (arePlanIdOrdersEqual(currentOrder, nextOrder)) {
      return;
    }

    reset({
      planFields: nextOrder.map(id => ({
        id,
        selected: selectedById.has(id) ? Boolean(selectedById.get(id)) : selectedPlans.includes(id),
      })),
    });
  }, [
    displayOrder,
    getValues,
    isDisplayOrderSuccess,
    orderedPlanIds,
    planIds,
    reset,
    selectedPlans,
  ]);

  const plansById = new Map(plans.map(plan => [plan.id, plan]));
  const canReorder =
    isAdviserArea && isDisplayOrderSuccess && controlledFields.length >= PLANS_MIN_COUNT;

  const { mutate: saveDisplayOrder } = useSaveComparePlansDisplayOrderMutation(clientId);

  const selectedPlansCount = controlledFields.filter(field => field.selected).length;

  const onSubmit = handleSubmit((values: FormValues) => {
    const newSelectedPlans = values.planFields
      .filter(field => field.selected)
      .map(field => field.id);

    if (canReorder) {
      saveDisplayOrder(
        values.planFields.map(field => field.id),
        {
          onSuccess: () => {
            hasUserReorderedRef.current = false;
          },
          onError: () => {
            toast.error(MESSAGES.GENERIC_ERROR);
          },
        },
      );
    }

    onSave(newSelectedPlans);
    onClose();
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !canReorder) {
      return;
    }

    const currentPlanIds = controlledFields.map(field => field.id);
    const oldIndex = currentPlanIds.indexOf(String(active.id));
    const newIndex = currentPlanIds.indexOf(String(over.id));

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    hasUserReorderedRef.current = true;
    move(oldIndex, newIndex);
  };

  return {
    ...formBag,
    fields: controlledFields,
    plansById,
    selectedPlansCount,
    canReorder,
    onSubmit,
    handleDragEnd,
  };
};
